import {CurrentUserService} from "../../../../services/user/CurrentUserService";
import {UserStripeService} from "../../../../stripe/service/UserStripeService";
import {ClientDashboardModel} from "../../models/ClientDashboardModel";
import {StripeProductName} from "../../../../stripe/models/StripeProductNames";
import {ClientDashboardModelProvider} from "../ClientDashboardModelProvider";
import {DepositCmaHelper} from "../../../../help/deposit-cma/DepositCmaHelper";
import {IvProductMappings, IvStoreProduct} from "../../../../store/iv/models/IvStoreProduct";
import {ObjectUtil} from "../../../../js/util/ObjectUtil";
import {FeatureNames} from "../../../../types/UserConfig";
import {AccountStore} from "../../../../account/store/AccountStore";
import {isNotNull} from "../../../../js/util/UtilFunctions";

export class IVClientDashboardModelProvider extends ClientDashboardModelProvider {

    public static id = "ivClientDashboardModelProvider";

    /** @ngInject */
    public constructor(private readonly currentUserService: CurrentUserService,
                       private readonly depositCmaHelper: DepositCmaHelper,
                       private readonly userStripeService: UserStripeService,
                       private readonly accountStore: AccountStore) {
        super();
    }

    public async model(): Promise<ClientDashboardModel> {
        const productHistory = await this.userStripeService.productHistory();
        const platformStatus = this.buildFeatureStatus(StripeProductName.PLATFORM, productHistory);
        const tradingSystemsStatus = this.buildFeatureStatus(StripeProductName.TRADING_SYSTEMS_ALERTS, productHistory);
        const portfolio = this.activeFeature();
        const strategyBuilder = this.mergeStates(platformStatus, tradingSystemsStatus);
        const backtesting = this.buildFeatureStatus(StripeProductName.BACKTESTING, productHistory);
        const charting = ObjectUtil.copy(platformStatus);

        const billing = await this.userStripeService.billingDetails();
        const card = billing.card;

        const hasTradingAlerts = this.currentUserService.hasFeature(FeatureNames.TRADING_ALERTS);
        const showTradingIdeas = this.currentUserService.hasFeature(FeatureNames.LITT_TRADING_SYSTEM);

        return {
            showMarginLimit: true,
            showHelp: true,
            showTradingIdeas,
            showMarketSummary: true,
            openCmaDeposit: () => this.openCmaDepositDialog(),
            hasTradingAlerts,

            features: {
                hasBillingDetails: card != null,
                portfolio,
                charting,
                strategyBuilder,
                backtesting
            }
        };
    }

    private openCmaDepositDialog() {
        this.accountStore
            .current
            .take(1)
            .filter(isNotNull)
            .subscribe((p) => this.depositCmaHelper.open(p.id));
    }

    protected storeUrl(name: StripeProductName): string | null {
        switch (name) {
            case StripeProductName.BACKTESTING:
                return `store?product=${IvStoreProduct.BACKTESTING}`;
            case StripeProductName.PLATFORM:
                return `store?product=${IvStoreProduct.PRO}`;
            case StripeProductName.TRADING_SYSTEMS_ALERTS:
                return `store?product=${IvStoreProduct.TRADING_SYSTEMS}`;
            default:
                return null;
        }
    }

    protected trialPeriod(name: StripeProductName): number {
        switch (name) {
            case StripeProductName.BACKTESTING:
                return IvProductMappings.BACKTESTING.trial;
            case StripeProductName.PLATFORM:
                return IvProductMappings.PRO.trial;
            case StripeProductName.TRADING_SYSTEMS_ALERTS:
                return IvProductMappings.TRADING_SYSTEMS.trial;
            default:
                return 0;
        }
    }
}
